import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  channelGoneMeta,
  channelLookupUrl,
  channelMetaFrom,
  DEFAULT_SEARCH_QUERY,
  resolveInstanceUrl,
  subscriptionRows,
  VISIOUS_SCOPES,
  type InstanceConfig,
} from '@visious/core';
import { changeSubscription } from '../../api/auth';
import { fetchChannelDetails } from '../../api/channels';
import { fetchResolvedTarget } from '../../api/resolve';
import { fetchSearch } from '../../api/search';
import { loadSubscriptions, subscriptionsListKey } from '../../subscriptions/list';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import { putChannelMeta, touchChannelMeta, useChannelMetaCache } from '../../platform/channelMetaStore';
import { useSession } from '../../platform/session';
import { text } from '../../strings';
import type { ProblemCopy } from '../../api/problem';
import {
  candidateFromDetails,
  candidatesFromSearch,
  classifyAddInput,
  manageListState,
  type CandidateRow,
  type ManageListState,
  type ManageSort,
} from './model';

/**
 * The React binding for the manage-subscriptions page. The lazy avatar hydration is the machinery the
 * feed page's channel strip used to run (3b) — it moved here whole when the strip did: names render
 * from the first response, a channel record (~50 KB) is fetched only for rows that scroll into view,
 * through the R14 cache.
 */

/** What the add card is showing. */
export type FindState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'results'; readonly rows: readonly CandidateRow[] }
  | { readonly kind: 'nothing'; readonly q: string }
  /** A pasted link that resolves to something that is not a channel — a Short, a search, home. */
  | { readonly kind: 'not-resolvable' }
  | { readonly kind: 'problem'; readonly detail: string };

export interface ManageSubscriptionsBinding {
  readonly list: ManageListState;
  readonly retry: () => void;
  readonly observeChannel: (channelId: string, element: Element | null) => void;
  /** The grid's filter — display-only, so it is state here and never in the URL (unlike a search). */
  readonly filter: string;
  readonly setFilter: (value: string) => void;
  /** The grid's sort — display-only for the filter's reason. */
  readonly sort: ManageSort;
  readonly setSort: (value: ManageSort) => void;
  /** One tile's unsubscribe — the selection is retired, and each card carries its own button. */
  readonly remove: (channelId: string) => void;
  /** The unsubscribes in flight, by channel id — each tile's button disables itself alone. */
  readonly removing: ReadonlySet<string>;
  readonly removeProblem: ProblemCopy | null;
  readonly addInput: string;
  readonly setAddInput: (value: string) => void;
  readonly find: () => void;
  readonly findState: FindState;
}

export function useManageSubscriptions(): ManageSubscriptionsBinding {
  const session = useSession();
  const client = useQueryClient();
  const grantKey = session.grantedAtEpochSec ?? 0;

  const subsQuery = useQuery({
    queryKey: subscriptionsListKey(grantKey),
    queryFn: () => loadSubscriptions(),
    retry: false,
    staleTime: 30 * 60 * 1000,
    enabled: session.ready && session.facts.signedIn,
  });
  const subscriptions = subsQuery.data?.ok === true ? subsQuery.data.subscriptions : null;
  const subsProblem = subsQuery.data?.ok === false ? subsQuery.data.problem : null;

  /* -- lazy avatars, moved whole from the feed page's strip (3b) --------------------------------- */

  const cache = useChannelMetaCache();
  const { visible, observeChannel } = useVisibleChannels();
  const inFlight = useRef(new Set<string>());
  const [config, setConfig] = useState<InstanceConfig | null>(null);

  useEffect(() => {
    let live = true;
    void getPlatform().then((boot) => {
      if (live && boot.ok) setConfig(boot.platform.config);
    });
    return () => {
      live = false;
    };
  }, []);

  const nowEpochSec = Math.floor(systemClock.nowEpochMs() / 1000);
  const hydration = useMemo(
    () =>
      subscriptions === null
        ? null
        : subscriptionRows({ subscriptions, cache, visible, nowEpochSec }),
    // `nowEpochSec` is deliberately out of the list: it changes every render and only decides TTL
    // staleness, which does not need second resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptions, cache, visible],
  );

  const toFetch = hydration?.fetch ?? EMPTY;
  useEffect(() => {
    if (toFetch.length === 0) return;

    for (const channelId of toFetch) {
      if (inFlight.current.has(channelId)) continue;
      inFlight.current.add(channelId);

      void (async () => {
        try {
          const boot = await getPlatform();
          if (!boot.ok) return;
          const result = await fetchChannelDetails(boot.platform, channelId);
          const nowSec = Math.floor(boot.platform.clock.nowEpochMs() / 1000);
          if (!result.ok) {
            // A deleted or terminated channel is a durable fact, so it gets a tombstone rather than
            // a dropped fetch: an uncached miss is re-asked every time the cache moves, and dead
            // channels used to be re-fetched without end. Any other
            // failure stays uncached and is retried the ordinary way.
            if (
              result.error.kind === 'transport' &&
              result.error.error.kind === 'video-unavailable' &&
              result.error.error.notFound === 'channel'
            ) {
              putChannelMeta([channelGoneMeta(channelId, nowSec)]);
            }
            return;
          }
          putChannelMeta([channelMetaFrom(boot.platform.config, result.value, nowSec)]);
        } finally {
          inFlight.current.delete(channelId);
        }
      })();
    }
    // No cleanup on purpose — the guard this omits caused the one-avatar bug the strip documented:
    // the first response re-runs the effect and a `live = false` would discard the others for good.
  }, [toFetch]);

  const renderedIds = hydration?.rows.filter((row) => row.hydrated).map((row) => row.channelId);
  useEffect(() => {
    if (renderedIds !== undefined && renderedIds.length > 0) {
      touchMetaSafely(renderedIds);
    }
    // Keyed by the id list rather than by the array, which is new on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedIds?.join(',')]);

  const resolveAsset = useCallback(
    (path: string) => (config === null ? null : resolveInstanceUrl(config, path)),
    [config],
  );

  /* -- removal -------------------------------------------------------------------------------------- */

  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<ManageSort>('default');
  const [removing, setRemoving] = useState<ReadonlySet<string>>(NONE);
  const [removeProblem, setRemoveProblem] = useState<ProblemCopy | null>(null);

  const remove = useCallback(
    (channelId: string) => {
      if (removing.has(channelId)) return;
      setRemoveProblem(null);
      setRemoving((previous) => new Set(previous).add(channelId));

      void (async () => {
        let failed = false;
        try {
          const boot = await getPlatform();
          if (!boot.ok) {
            failed = true;
            return;
          }
          const result = await changeSubscription(boot.platform, 'unsubscribe', channelId, VISIOUS_SCOPES);
          failed = !result.ok;
        } finally {
          setRemoving((previous) => {
            const next = new Set(previous);
            next.delete(channelId);
            return next;
          });
          if (failed) setRemoveProblem(text().subscriptions.manage.list.removeFailed);
          void client.invalidateQueries({ queryKey: subscriptionsListKey(grantKey) });
        }
      })();
    },
    [client, grantKey, removing],
  );

  const list = useMemo(
    () =>
      manageListState({
        rows: hydration?.rows ?? null,
        problem: subsProblem,
        filter,
        sort,
        resolveAsset,
      }),
    [filter, hydration, resolveAsset, sort, subsProblem],
  );

  /* -- the add card -------------------------------------------------------------------------------- */

  const [addInput, setAddInput] = useState('');
  const [findState, setFindState] = useState<FindState>(IDLE);
  /** Stamped per submission so a slow answer cannot overwrite a newer question's. */
  const findSeq = useRef(0);

  const find = useCallback(() => {
    const query = classifyAddInput(addInput);
    if (query.kind === 'empty') {
      setFindState(IDLE);
      return;
    }

    const seq = ++findSeq.current;
    const settle = (state: FindState): void => {
      if (findSeq.current === seq) setFindState(state);
    };
    setFindState({ kind: 'busy' });

    void (async () => {
      const boot = await getPlatform();
      if (!boot.ok) {
        settle({ kind: 'problem', detail: text().subscriptions.manage.add.notResolvable });
        return;
      }
      const platform = boot.platform;

      const candidateFor = async (channelId: string): Promise<FindState> => {
        const details = await fetchChannelDetails(platform, channelId);
        return details.ok
          ? { kind: 'results', rows: [candidateFromDetails(details.value)] }
          : { kind: 'not-resolvable' };
      };

      switch (query.kind) {
        case 'channel':
          settle(await candidateFor(query.channelId));
          return;

        case 'name': {
          const lookupUrl = channelLookupUrl(query.route);
          if (lookupUrl === null) {
            settle({ kind: 'not-resolvable' });
            return;
          }
          const resolved = await fetchResolvedTarget(platform, lookupUrl);
          if (!resolved.ok || resolved.value.kind !== 'channel') {
            settle({ kind: 'not-resolvable' });
            return;
          }
          settle(await candidateFor(resolved.value.channelId));
          return;
        }

        case 'search': {
          const result = await fetchSearch(
            platform,
            { ...DEFAULT_SEARCH_QUERY, q: query.q, type: 'channel' },
            null,
          );
          if (!result.ok) {
            settle({ kind: 'problem', detail: text().subscriptions.manage.add.nothing(query.q) });
            return;
          }
          const rows = candidatesFromSearch(result.value.items).slice(0, MAX_CANDIDATES);
          settle(rows.length === 0 ? { kind: 'nothing', q: query.q } : { kind: 'results', rows });
          return;
        }
      }
    })();
  }, [addInput]);

  return {
    list,
    retry: useCallback(() => void subsQuery.refetch(), [subsQuery]),
    observeChannel,
    filter,
    setFilter,
    sort,
    setSort,
    remove,
    removing,
    removeProblem,
    addInput,
    setAddInput: useCallback((value: string) => {
      setAddInput(value);
      // Typing again after an answer invalidates the answer — the same rule the search box follows.
      findSeq.current += 1;
      setFindState(IDLE);
    }, []),
    find,
    findState,
  };
}

const EMPTY: readonly string[] = [];
const NONE: ReadonlySet<string> = new Set<string>();
const IDLE: FindState = { kind: 'idle' };

/** Enough rows to find the channel meant, few enough that each Subscribe button stays deliberate. */
const MAX_CANDIDATES = 8;

function touchMetaSafely(ids: readonly string[]): void {
  touchChannelMeta(ids, Math.floor(systemClock.nowEpochMs() / 1000));
}

/**
 * Which rows are on screen — the strip's own `IntersectionObserver` machinery, moved with it.
 * A row that leaves the viewport stays in the set: what the set gates is the *first* fetch.
 */
function useVisibleChannels(): {
  readonly visible: readonly string[];
  readonly observeChannel: (channelId: string, element: Element | null) => void;
} {
  const [visible, setVisible] = useState<readonly string[]>(EMPTY);
  const seen = useRef(new Set<string>());
  const elements = useRef(new Map<Element, string>());
  const observer = useRef<IntersectionObserver | null>(null);

  const markSeen = useCallback((channelId: string) => {
    if (seen.current.has(channelId)) return;
    seen.current.add(channelId);
    setVisible([...seen.current]);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  const observeChannel = useCallback(
    (channelId: string, element: Element | null) => {
      if (element === null) return;

      if (observer.current === null) {
        if (typeof IntersectionObserver === 'undefined') {
          markSeen(channelId);
          return;
        }
        observer.current = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const seenId = elements.current.get(entry.target);
              if (seenId !== undefined) markSeen(seenId);
            }
          },
          { rootMargin: '200px' },
        );
      }

      elements.current.set(element, channelId);
      observer.current.observe(element);
    },
    [markSeen],
  );

  return { visible, observeChannel };
}
